import { Alert, Box, CircularProgress, Container } from '@mui/material';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import FinalizedLifecycleReplayPresentation from '../components/assessment/FinalizedLifecycleReplayPresentation';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import {
    FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION_IDS,
    isFinalizedLifecycleReplayOrganization,
} from '../RuntimeConfig';
import { Securables } from '../Enums';
import FinalizedLifecycleReplayService, {
    expectedFinalizedLifecycleReplayFingerprint,
    FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION,
    FINALIZED_LIFECYCLE_REPLAY_VERSION,
    FinalizedLifecycleReplay,
    finalizedLifecycleReplayFingerprint,
} from '../services/FinalizedLifecycleReplayService';

export const isCaptureSafeFinalizedLifecycleReplay = (
    replay: FinalizedLifecycleReplay,
    selectedOrganizationId: string,
    selectedOrganizationName: string = replay.OrganizationName,
) => replay.Version === FINALIZED_LIFECYCLE_REPLAY_VERSION
    && replay.IsSynthetic === true
    && replay.ReadOnly === true
    && replay.OrganizationId === selectedOrganizationId
    && replay.OrganizationName === FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION
    && isFinalizedLifecycleReplayOrganization(
        selectedOrganizationId,
        selectedOrganizationName,
        FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION_IDS,
    )
    && finalizedLifecycleReplayFingerprint(replay) === expectedFinalizedLifecycleReplayFingerprint();

const FinalizedLifecycleReplayPage = () => {
    const { accessInfo } = useAccessInfo();
    const selectedOrganizationId = accessInfo?.selectedOrganizationId ?? '';
    const selectedOrganizationName = accessInfo?.selectedOrganizationName ?? '';
    const [replay, setReplay] = useState<FinalizedLifecycleReplay | null>(null);
    const [loadedOrganizationId, setLoadedOrganizationId] = useState('');
    const [failedOrganizationId, setFailedOrganizationId] = useState('');

    useEffect(() => {
        const controller = new AbortController();
        setReplay(null);
        setLoadedOrganizationId('');
        setFailedOrganizationId('');

        new FinalizedLifecycleReplayService().Get({ selectedOrganizationId, selectedOrganizationName }, controller.signal)
            .then(result => {
                if (controller.signal.aborted)
                    return;
                if (!isCaptureSafeFinalizedLifecycleReplay(result, selectedOrganizationId, selectedOrganizationName)) {
                    setFailedOrganizationId(selectedOrganizationId);
                    return;
                }
                setReplay(result);
                setLoadedOrganizationId(selectedOrganizationId);
            })
            .catch(() => {
                if (!controller.signal.aborted)
                    setFailedOrganizationId(selectedOrganizationId);
            });

        return () => controller.abort();
    }, [selectedOrganizationId, selectedOrganizationName]);

    if (failedOrganizationId === selectedOrganizationId)
        return <Container maxWidth="md" sx={{ py: 4 }}><Alert severity="warning">Finalized lifecycle replay is unavailable for this organization.</Alert></Container>;

    if (!replay || loadedOrganizationId !== selectedOrganizationId)
        return <Box role="status" aria-label="Loading finalized lifecycle replay" sx={{ minHeight: 300, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

    return <FinalizedLifecycleReplayPresentation replay={replay} />;
};

export const FinalizedLifecycleReplayRoute = () => {
    const { accessInfo, hasOneOfRoles } = useAccessInfo();
    const isAuthorized = hasOneOfRoles([Securables.organization_administrator, Securables.synergist_admin], true);
    const isAllowlistedOrganization = isFinalizedLifecycleReplayOrganization(
        accessInfo?.selectedOrganizationId ?? '',
        accessInfo?.selectedOrganizationName ?? '',
        FINALIZED_LIFECYCLE_REPLAY_ORGANIZATION_IDS,
    );

    if (!isAuthorized || !isAllowlistedOrganization)
        return <Redirect to="/" />;

    return <FinalizedLifecycleReplayPage />;
};

export default FinalizedLifecycleReplayPage;
